/**
 * AppAdoptionModal — adotar ou descartar uma app criada fora do pipeline.
 *
 * App com `ztna_provision_jobs` vazio não responde a nenhum dos três verbos de
 * ciclo de vida: todos passam por `_last_successful_provision` e devolvem 422.
 * Em 27/08/2026 isso travou quatro tentativas seguidas na app 87, que estava em
 * 503 — sem caminho para consertar nem para descartar.
 *
 * A tela não decide nada: ela mostra o que o preview MEDIU e o que a adoção
 * FARIA, campo a campo com a origem de cada valor, e o backend recalcula tudo
 * ao receber o pedido. Três dos sete estados não oferecem ação nenhuma — e
 * nesses o botão não existe, em vez de existir desabilitado: botão desabilitado
 * convida a insistir.
 */
import { useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  List,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconArchive, IconPlugConnected } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { formatApiError } from '@/api/errors'
import { ztnaApi } from '../api'
import type { AdoptionPreview, AppHealthSummary } from '../types'

interface Props {
  app: AppHealthSummary
  opened: boolean
  onClose: () => void
}

/** O que a tela oferece em cada estado. Sai do que foi MEDIDO, não de uma lista
 *  de app ids — é o que faz a regra continuar valendo para a próxima app que
 *  nascer fora do pipeline. */
const EXPLICACAO: Record<AdoptionPreview['estado'], { titulo: string; texto: string; adotar: boolean }> = {
  ja_no_ztna: {
    titulo: 'Já responde pelo broker',
    texto:
      'O hostname resolve no balanceador da plataforma e o broker respondeu. Adotar aqui '
      + 'apenas registra o que já é verdade: nada em AWS será tocado.',
    adotar: true,
  },
  na_origem: {
    titulo: 'Aponta para a origem',
    texto:
      'O hostname resolve fora da plataforma. Adotar vai provisionar certificado, regra de '
      + 'balanceador e reapontar o DNS para o broker.',
    adotar: true,
  },
  no_broker_antigo: {
    titulo: 'Aponta para o balanceador antigo',
    texto:
      'O hostname ainda resolve no balanceador descomissionado — é o que devolve 503. Adotar '
      + 'vai provisionar e trazer o DNS para o broker.',
    adotar: true,
  },
  servido_pela_plataforma: {
    titulo: 'Servida pela plataforma, sem broker no caminho',
    texto:
      'Este hostname resolve no balanceador da plataforma e responde sem passar pelo broker — '
      + 'é o próprio console ou outra aplicação do mesh, não uma aplicação de cliente. Adotar '
      + 'reescreveria o DNS dela.',
    adotar: false,
  },
  registro_ausente: {
    titulo: 'Não existe registro A para este hostname',
    texto:
      'Sem o registro na zona descoberta, o provisionamento falharia no meio — depois de já ter '
      + 'aberto a mudança. Crie o registro na conta do tenant antes de adotar.',
    adotar: false,
  },
  nao_medido: {
    titulo: 'Não deu para medir',
    texto:
      'A sonda não conseguiu determinar se o broker está no caminho deste hostname. Sem isso não '
      + 'há como saber o que a adoção deve fazer.',
    adotar: false,
  },
  zona_indecidivel: {
    titulo: 'Não dá para decidir a zona DNS',
    texto:
      'Ou nenhuma zona da conta do tenant casa com este hostname, ou mais de uma casa igualmente '
      + 'bem. Escrever DNS na zona errada é silencioso — resolva na conta do tenant antes.',
    adotar: false,
  },
}

const ROTULO_DA_ORIGEM: Record<string, string> = {
  coluna: 'do cadastro da aplicação',
  descoberta: 'descoberto no Route53 do tenant',
  derivado: 'derivado',
}

export function AppAdoptionModal({ app, opened, onClose }: Props) {
  const queryClient = useQueryClient()
  const [confirmText, setConfirmText] = useState('')

  const preview = useQuery({
    queryKey: ['ztna', 'adoption-preview', app.id],
    queryFn: () => ztnaApi.adoptionPreview(app.id),
    enabled: opened,
    // Custa duas chamadas AWS e uma sonda: não refaz sozinho.
    refetchOnWindowFocus: false,
    retry: false,
  })

  const podeConfirmar = confirmText === app.hostname

  const adotar = useMutation({
    mutationFn: () => ztnaApi.adoptApp(app.id, app.hostname),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      notifications.show({
        color: 'teal',
        title: r.mode === 'registro' ? 'Aplicação adotada' : 'Adoção iniciada',
        message:
          r.mode === 'registro'
            ? 'O histórico foi registrado a partir do que foi medido. Nada em AWS foi alterado.'
            : r.status === 'pending_gmud'
              ? `O provisionamento espera aprovação da mudança${r.gmud_id ? ` (GMUD ${r.gmud_id})` : ''}.`
              : `Job ${r.job_id} disparado: certificado, DNS e regra de balanceador.`,
      })
      onClose()
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Não foi possível adotar',
        message: formatApiError(err, 'Não foi possível adotar a aplicação.'),
      })
    },
  })

  const descartar = useMutation({
    mutationFn: () => ztnaApi.discardApp(app.id, app.hostname),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      notifications.show({
        color: 'orange',
        title: 'Aplicação descartada do catálogo',
        message:
          `Ela sai da lista. Continuam de pé: ${r.nao_tocado.join(', ')} — nada em AWS foi tocado.`,
      })
      onClose()
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Não foi possível descartar',
        message: formatApiError(err, 'Não foi possível descartar a aplicação.'),
      })
    },
  })

  const dados = preview.data
  const explicacao = dados ? EXPLICACAO[dados.estado] : null
  const modoDeAdocao = dados?.estado === 'ja_no_ztna' ? 'registro' : 'execucao'

  return (
    <Modal opened={opened} onClose={onClose} title={`Adotar ${app.slug}`} size="xl">
      <Stack gap="md">
        {preview.isLoading && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Descobrindo a zona DNS na conta do tenant e medindo se o broker está no caminho…
            </Text>
          </Group>
        )}

        {preview.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Não deu para olhar">
            {formatApiError(preview.error, 'Não foi possível consultar o estado desta aplicação.')}
          </Alert>
        )}

        {dados && explicacao && (
          <>
            <Alert
              color={explicacao.adotar ? 'blue' : 'orange'}
              variant="light"
              title={explicacao.titulo}
            >
              <Stack gap={6}>
                <Text size="sm">{explicacao.texto}</Text>
                {dados.motivo && (
                  <Text size="sm" ff="JetBrains Mono, monospace">{dados.motivo}</Text>
                )}
                {dados.alvo_atual && (
                  <Text size="xs" c="dimmed">
                    Hoje o registro aponta para <Code>{dados.alvo_atual}</Code>.
                  </Text>
                )}
                {/* A hora da medição: sem ela, não dá para saber se o que está na
                    tela é de agora ou de uma varredura antiga. */}
                {dados.medido_em && (
                  <Text size="xs" c="dimmed" data-testid="medido-em">
                    Medido em {dayjs(dados.medido_em).format('DD/MM/YYYY HH:mm')}.
                  </Text>
                )}
              </Stack>
            </Alert>

            {dados.payload_derivado && (
              <>
                <Text size="sm" fw={600}>
                  O que será usado no provisionamento, e de onde veio cada valor
                </Text>
                <Table striped withTableBorder>
                  <Table.Tbody>
                    {Object.entries(dados.payload_derivado).map(([campo, valor]) => (
                      <Table.Tr key={campo}>
                        <Table.Td w={200}>
                          <Text size="xs" ff="JetBrains Mono, monospace">{campo}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ff="JetBrains Mono, monospace">
                            {typeof valor === 'object' ? JSON.stringify(valor) : String(valor)}
                          </Text>
                        </Table.Td>
                        <Table.Td w={220}>
                          <Badge
                            size="xs"
                            variant="light"
                            color={dados.origem_dos_campos[campo] === 'coluna' ? 'gray' : 'inovareTeal'}
                            data-testid={`origem-${campo}`}
                          >
                            {ROTULO_DA_ORIGEM[dados.origem_dos_campos[campo]] ?? dados.origem_dos_campos[campo]}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}

            {explicacao.adotar && (
              <Stack gap={6}>
                {modoDeAdocao === 'execucao' && (
                  <Text size="sm">
                    Para confirmar, digite o hostname exato:
                  </Text>
                )}
                {modoDeAdocao === 'registro' && (
                  <Text size="sm">
                    Nada em AWS será alterado. Para confirmar, digite o hostname exato:
                  </Text>
                )}
                <Text size="xs" ff="JetBrains Mono, monospace" c="dimmed">
                  {app.hostname}
                </Text>
                <TextInput
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.currentTarget.value)}
                  placeholder={app.hostname}
                  ff="JetBrains Mono, monospace"
                />
              </Stack>
            )}

          </>
        )}

        {/* FORA do bloco do preview, de propósito. O descarte não faz nenhuma
            chamada à AWS, e até 31/08/2026 ele estava condicionado ao sucesso do
            `adoption-preview`, que chama `AssumeRole` na conta do tenant. Nos
            tenants 3 e 93 o preview falha, e o único verbo de saída da app ficava
            indisponível justamente ali. O gate agora é `app.tem_historico`, que a
            listagem já entregou.

            O alerta acompanha o botão: botão sem a explicação do que NÃO é
            tocado mente por omissão — a app some da lista e o hostname continua
            respondendo. */}
        {!app.tem_historico && (
          <Alert color="gray" variant="light" title="Descartar do catálogo">
            <Stack gap={6}>
              <Text size="sm">
                Tira a aplicação da lista sem tocar em nada na AWS. O que continua de pé:
              </Text>
              <List size="sm" spacing={2}>
                <List.Item>o registro DNS continua onde está</List.Item>
                <List.Item>o certificado continua emitido</List.Item>
                <List.Item>a regra do balanceador continua lá</List.Item>
              </List>
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          {!app.tem_historico && (
            <Button
              color="orange"
              variant="light"
              leftSection={<IconArchive size={14} />}
              onClick={() => descartar.mutate()}
              loading={descartar.isPending}
            >
              Descartar do catálogo
            </Button>
          )}
          {/* Nos estados que não permitem adoção o botão NÃO existe — desabilitado
              convidaria a insistir numa ação que o backend recusa. */}
          {explicacao?.adotar && (
            <Button
              color="inovareTeal"
              leftSection={<IconPlugConnected size={14} />}
              onClick={() => adotar.mutate()}
              loading={adotar.isPending}
              disabled={!podeConfirmar}
            >
              Adotar
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  )
}

export default AppAdoptionModal
